import { FC } from 'react'
import {
  Body1,
  Box,
  Button,
  Chip,
  DialogTrigger,
  findBy,
  H3,
  Link,
  makeStyles,
} from '@perk-ui/core'
import { Edit, Trash2 } from 'react-feather'

import DataPair from '../../../components/DataPair'
import { useSitesContext } from '../../../components/SitesProvider'
import { ProtocolTreatment } from '../../../features/query-hooks/useProtocols'
import useProviders from '../../../features/query-hooks/useProviders'
import { buildName } from '../../../utils/buildName'

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  treatmentDate: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  editButton: {
    marginLeft: theme.spacing(3),
  },
  dialogTrigger: {
    display: 'inline',
  },
  deleteButton: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    marginLeft: theme.spacing(3),
  },
  dialogContent: {
    '& > p': {
      textAlign: 'center',
    },
  },
  dialogButtons: {
    display: 'flex',
    marginTop: theme.spacing(3),
    justifyContent: 'center',
    '& button+button': {
      marginLeft: theme.spacing(5),
    },
  },
  accordion: {
    // Ovverrides of MUI styles
    '&&': {
      boxShadow: 'none',
      margin: 0,
      '&:before': {
        content: 'none',
      },
    },
  },
  accordionSummary: {
    // Overrides of MUI styles
    '&&': {
      paddingLeft: 0,
      minHeight: 0,
      '& > *': {
        margin: 0,
      },
    },
  },
  accordionTitle: {
    fontWeight: 500,
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',

    '&&': {
      padding: 0,
      paddingTop: theme.spacing(1),
    },
  },
  dataPair: {
    '& p.MuiTypography-root': {
      marginBottom: theme.spacing(1),
      '& span': {
        display: 'block',
        fontWeight: 700,
      },
    },
  },
  periodChip: {
    backgroundColor: '#269DD924',
    color: '#2396CF',
    fontSie: '16px',
    fontWeight: 400,
    marginLeft: theme.spacing(1),
  },
}))

export interface TreatmentDetailProps {
  treatment: ProtocolTreatment
  onEditClick?: () => void
  onDeleteClick?: () => void
}

const TreatmentDetail: FC<TreatmentDetailProps> = ({
  treatment,
  onEditClick,
  onDeleteClick,
}) => {
  const classes = useStyles()
  const { currentSite } = useSitesContext()
  const { data: providers = [] } = useProviders()

  const treatmentLocation = findBy(
    'id',
    treatment?.siteLocationId,
    currentSite?.siteLocations || [],
  )
  const treatmentProvider = findBy('id', treatment?.staffId, providers)

  if (!treatment) return null

  return (
    <>
      <Box
        pr={2}
        display="flex"
        justifyContent="space-between"
        flexDirection="row"
        pt={2}
        pb={1}
      >
        <H3 sx={{ pointerEvents: 'none' }}>
          {treatment.order == 1 ? 'Primary Treatment' : 'Additional Treatment'}
          {treatment.order != 1 ? (
            <Chip
              className={classes.periodChip}
              size="small"
              label={`+ ${treatment.periodAmount} ${treatment.periodType}`}
            />
          ) : (
            ''
          )}
        </H3>
        <Box display="flex">
          {onEditClick && (
            <Link
              className={classes.editButton}
              component="button"
              underline="hover"
              onClick={onEditClick}
            >
              <Edit />
            </Link>
          )}
          {onDeleteClick && treatment.order !== 1 && (
            <DialogTrigger
              action={
                <Link
                  component={Body1}
                  className={classes.deleteButton}
                  underline="hover"
                >
                  <Trash2 />
                </Link>
              }
              content={(toggle) => (
                <Box p={3} className={classes.dialogContent}>
                  <Body1>Are you sure you want to delete this treatment?</Body1>
                  <div className={classes.dialogButtons}>
                    <Button
                      size="medium"
                      variant="outlined"
                      onClick={onDeleteClick}
                    >
                      Confirm
                    </Button>
                    <Button size="medium" onClick={() => toggle()}>
                      Cancel
                    </Button>
                  </div>
                </Box>
              )}
            />
          )}
        </Box>
      </Box>
      <Box className={classes.dataPair}>
        {treatment.siteLocationId &&
          treatmentLocation &&
          treatmentLocation.name && (
            <DataPair label="Clinic Location" value={treatmentLocation.name} />
          )}
        {treatmentProvider && (
          <DataPair label="Provider" value={buildName(treatmentProvider)} />
        )}
        {treatmentProvider && (
          <DataPair
            label="Treatment Type"
            value={treatment.medicalTreatment?.name}
          />
        )}
        {treatment.treatmentProduct && (
          <DataPair
            label="Treatment Product"
            value={treatment.treatmentProduct.name}
          />
        )}
      </Box>
    </>
  )
}

export default TreatmentDetail
